(() => {
  const instances = new Map();

  class AWMainProduct {
    constructor(root) {
      this.root = root;
      this.buttonsBlock = null;
      this.observer = null;
      this.mobileThumbObserver = null;
      this.timesactController = null;
      this.unisizeController = null;
      this.wishlistController = null;
      this.syncTimerIds = [];
      this.mutationFrame = null;
      this.scrollFrame = null;
      this.resizeFrame = null;
      this.currentModal = null;
      this.pendingCartRedirect = false;
      this.cartRedirectRequestId = 0;
      this.cartRedirectTimeoutId = null;
      this.mobileThumbResetTimerIds = [];
      this.preorderDescriptionMap = this.parsePreorderDescriptionMap();
      this.preorderPlanMap = this.parsePreorderPlanMap();
      this.variantAvailabilityMap = this.parseVariantAvailabilityMap();
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onMutate = this.onMutate.bind(this);
      this.onWindowScroll = this.onWindowScroll.bind(this);
      this.onWindowResize = this.onWindowResize.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onCartChange = this.onCartChange.bind(this);

      this.refreshRefs();
      this.initAppControllers();
      this.bind();
      this.sync();
      this.scheduleSync([80, 180, 320]);
      this.scheduleMobileThumbReset([0, 120, 320, 700]);
    }

    refreshRefs() {
      this.buttonsBlock = this.root.querySelector(".aw-main-product__block--buy-buttons");
      this.stickyAddWrap = this.root.querySelector("[data-aw-sticky-add]");
      this.stickyAddButton = this.root.querySelector("[data-aw-sticky-add-button]");
      this.stickyAddButtonText = this.root.querySelector("[data-aw-sticky-add-button-text]");
      this.productForm = this.root.querySelector("form[data-product-form]");
      this.mobileThumbs = this.root.querySelector("[data-product-thumbs]");
      this.preorderDescription = this.root.querySelector("[data-aw-preorder-description]");
      this.variantTarget =
        this.root.querySelector(".aw-main-product__block--variant-picker")
        || this.root.querySelector(".aw-main-product__block--buy-buttons")
        || this.root.querySelector("[data-product-form-wrapper]");

      this.mediaStage = this.root.querySelector(".aw-main-product__media-stage");
      this.flickityViewport = this.root.querySelector(".flickity-viewport");

      this.observeProductArea();
      this.observeMobileThumbs();
      this.observeImageResize();
    }

    initAppControllers() {
      const controllers = window.AW || {};

      if (typeof controllers.MainProductTimesact === "function") {
        this.timesactController = new controllers.MainProductTimesact({
          root: this.root,
          getVariantState: () => this.getVariantState(),
          onStateChange: () => this.sync(),
          isElementVisible: (element) => this.isElementVisible(element)
        });
      }

      if (typeof controllers.MainProductUnisize === "function") {
        this.unisizeController = new controllers.MainProductUnisize({
          root: this.root
        });
      }

      if (typeof controllers.MainProductWishlist === "function") {
        this.wishlistController = new controllers.MainProductWishlist({
          root: this.root,
          requestSync: (delays) => this.scheduleSync(delays)
        });
      }
    }

    observeProductArea() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      this.observer = new MutationObserver(this.onMutate);
      this.observer.observe(this.root, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    observeMobileThumbs() {
      if (this.mobileThumbObserver) {
        this.mobileThumbObserver.disconnect();
        this.mobileThumbObserver = null;
      }

      if (!(this.mobileThumbs instanceof HTMLElement)) {
        return;
      }

      this.mobileThumbObserver = new MutationObserver((mutations) => {
        const hasThumbListChange = mutations.some(({ type }) => type === "childList");
        const hasActiveThumbChange = mutations.some(({ target, type, attributeName }) => {
          if (!(target instanceof Element)) {
            return false;
          }

          return type === "attributes"
            && attributeName === "class"
            && target.matches(".media__thumb");
        });

        if (hasActiveThumbChange) {
          this.syncMobileThumbFirstState();
        }

        if (hasThumbListChange) {
          this.scheduleMobileThumbReset([0, 120]);
        }
      });

      this.mobileThumbObserver.observe(this.mobileThumbs, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"]
      });
    }

    bind() {
      this.root.addEventListener("change", this.onChange, true);
      this.root.addEventListener("input", this.onChange, true);
      this.root.addEventListener("submit", this.onSubmit, true);
      this.root.addEventListener("click", this.onClick);
      window.addEventListener("scroll", this.onWindowScroll, { passive: true });
      window.addEventListener("resize", this.onWindowResize);
      document.addEventListener("keydown", this.onKeydown);
      document.addEventListener("theme:cart:change", this.onCartChange);
    }

    parsePreorderDescriptionMap() {
      const jsonNode = this.root.querySelector("[data-aw-preorder-description-map]");

      if (!(jsonNode instanceof HTMLScriptElement)) {
        return {};
      }

      try {
        const parsed = JSON.parse(jsonNode.textContent || "{}");
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch (_error) {
        return {};
      }
    }

    parsePreorderPlanMap() {
      const jsonNode = this.root.querySelector("[data-aw-preorder-plan-map]");

      if (!(jsonNode instanceof HTMLScriptElement)) {
        return {};
      }

      try {
        const parsed = JSON.parse(jsonNode.textContent || "{}");
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch (_error) {
        return {};
      }
    }

    parseVariantAvailabilityMap() {
      const jsonNode = this.root.querySelector("[data-aw-variant-availability-map]");

      if (!(jsonNode instanceof HTMLScriptElement)) {
        return {};
      }

      try {
        const parsed = JSON.parse(jsonNode.textContent || "{}");
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch (_error) {
        return {};
      }
    }

    destroy() {
      this.root.removeEventListener("change", this.onChange, true);
      this.root.removeEventListener("input", this.onChange, true);
      this.root.removeEventListener("submit", this.onSubmit, true);
      this.root.removeEventListener("click", this.onClick);
      window.removeEventListener("scroll", this.onWindowScroll);
      window.removeEventListener("resize", this.onWindowResize);
      document.removeEventListener("keydown", this.onKeydown);
      document.removeEventListener("theme:cart:change", this.onCartChange);

      if (this.observer) {
        this.observer.disconnect();
      }

      if (this.mobileThumbObserver) {
        this.mobileThumbObserver.disconnect();
      }

      this.timesactController?.destroy();
      this.unisizeController?.destroy();
      this.wishlistController?.destroy();
      this.timesactController = null;
      this.unisizeController = null;
      this.wishlistController = null;

      this.syncTimerIds.forEach((timerId) => window.clearTimeout(timerId));
      this.syncTimerIds = [];
      this.mobileThumbResetTimerIds.forEach((timerId) => window.clearTimeout(timerId));
      this.mobileThumbResetTimerIds = [];

      if (this.mutationFrame) {
        window.cancelAnimationFrame(this.mutationFrame);
      }

      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
      }

      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      if (this.cartRedirectTimeoutId) {
        window.clearTimeout(this.cartRedirectTimeoutId);
        this.cartRedirectTimeoutId = null;
      }

      if (this.currentModal) {
        this.closeModal(this.currentModal.id);
      }
    }

    onChange(event) {
      if (!this.shouldHandleChangeTarget(event.target)) {
        this.timesactController?.onVariantChange(this.getVariantState(), event.target);
        return;
      }

      this.sync();
      this.scheduleSync([40, 140, 280]);
      this.scheduleMobileThumbReset([40, 160, 360, 720]);

      this.timesactController?.onVariantChange(this.getVariantState(), event.target);
    }

    onClick(event) {
      const addButton = event.target.closest("[data-add-to-cart], button[name='add']");

      if (addButton && this.root.contains(addButton)) {
        this.prepareCartRedirect();
      }

      const stickyButton = event.target.closest("[data-aw-sticky-add-button]");

      if (stickyButton && this.root.contains(stickyButton)) {
        event.preventDefault();
        this.scrollToVariantSelection();
        return;
      }

      const modalOpenButton = event.target.closest("[data-aw-modal-open]");

      if (modalOpenButton && this.root.contains(modalOpenButton)) {
        event.preventDefault();
        this.openModal(modalOpenButton.dataset.awModalOpen || "");
        return;
      }

      const modalCloseButton = event.target.closest("[data-aw-modal-close]");

      if (modalCloseButton && this.root.contains(modalCloseButton)) {
        event.preventDefault();
        this.closeModal(modalCloseButton.dataset.awModalClose || "");
        return;
      }

      if (this.wishlistController?.handleClick(event)) {
        return;
      }
    }

    onSubmit(event) {
      if (!(event.target instanceof HTMLFormElement) || !this.root.contains(event.target)) {
        return;
      }

      if (this.productForm && event.target !== this.productForm) {
        return;
      }

      this.prepareCartRedirect();
    }

    onMutate(mutations = []) {
      if (this.shouldIgnoreMutations(mutations)) {
        return;
      }

      if (this.mutationFrame) {
        window.cancelAnimationFrame(this.mutationFrame);
      }

      this.mutationFrame = window.requestAnimationFrame(() => {
        this.mutationFrame = null;
        this.sync();
      });
    }

    onKeydown(event) {
      if (event.key !== "Escape" || !this.currentModal) {
        return;
      }

      this.closeModal(this.currentModal.id);
    }

    onWindowScroll() {
      if (this.scrollFrame) {
        return;
      }

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateStickyAddVisibility();
      });
    }

    onWindowResize() {
      if (this.resizeFrame) {
        return;
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.sync();
      });
    }

    scheduleSync(delays) {
      delays.forEach((delay) => {
        const timerId = window.setTimeout(() => {
          this.syncTimerIds = this.syncTimerIds.filter((id) => id !== timerId);
          this.sync();
        }, delay);

        this.syncTimerIds.push(timerId);
      });
    }

    scheduleMobileThumbReset(delays) {
      this.mobileThumbResetTimerIds.forEach((timerId) => window.clearTimeout(timerId));
      this.mobileThumbResetTimerIds = [];

      delays.forEach((delay) => {
        const timerId = window.setTimeout(() => {
          this.mobileThumbResetTimerIds = this.mobileThumbResetTimerIds.filter((id) => id !== timerId);
          this.resetMobileThumbScroll();
        }, delay);

        this.mobileThumbResetTimerIds.push(timerId);
      });
    }

    resetMobileThumbScroll() {
      if (!this.isMobileViewport() || !(this.mobileThumbs instanceof HTMLElement)) {
        return;
      }

      if (this.mobileThumbs.scrollLeft === 0) {
        return;
      }

      this.mobileThumbs.scrollLeft = 0;
    }

    syncMobileThumbFirstState() {
      const firstThumb = this.mobileThumbs instanceof HTMLElement
        ? this.mobileThumbs.querySelector(".media__thumb:first-child")
        : null;
      const isFirstThumbActive = firstThumb instanceof HTMLElement
        && firstThumb.classList.contains("is-activated");

      this.root.classList.toggle("aw-main-product--mobile-first-thumb-active", !!isFirstThumbActive);
    }

    syncLegendValues() {
      this.root.querySelectorAll(".selector-wrapper").forEach((wrapper) => {
        const valueTarget = wrapper.querySelector("[data-swapper-target]");

        if (!(valueTarget instanceof HTMLElement)) {
          return;
        }

        const checkedInput = wrapper.querySelector('input[type="radio"]:checked');

        if (checkedInput instanceof HTMLInputElement) {
          valueTarget.textContent = checkedInput.value;
          return;
        }

        const hiddenInput = wrapper.querySelector("[data-popout-input]");

        if (hiddenInput instanceof HTMLInputElement) {
          valueTarget.textContent = hiddenInput.value;
        }
      });
    }

    getCurrentVariantId() {
      if (!(this.productForm instanceof HTMLFormElement)) {
        return "";
      }

      const checkedVariantInput = this.productForm.querySelector("input[name='id']:checked, input[name='id[]']:checked");

      if (checkedVariantInput instanceof HTMLInputElement && checkedVariantInput.value) {
        return checkedVariantInput.value;
      }

      const variantInput = this.productForm.querySelector("select[name='id'], input[name='id'], select[name='id[]'], input[name='id[]']");

      if (
        (variantInput instanceof HTMLInputElement || variantInput instanceof HTMLSelectElement)
        && variantInput.value
      ) {
        return variantInput.value;
      }

      return "";
    }

    getVariantState() {
      const variantId = this.getCurrentVariantId();
      const preorderDescription = variantId
        ? String(this.preorderDescriptionMap?.[variantId] || "").trim()
        : "";
      const sellingPlanId = variantId ? String(this.preorderPlanMap?.[variantId] || "") : "";

      return {
        variantId,
        available: variantId !== "" ? this.variantAvailabilityMap?.[variantId] !== false : null,
        isPreorder: preorderDescription !== "" && sellingPlanId !== "",
        sellingPlanId
      };
    }

    ensurePreorderDescriptionNode() {
      if (this.preorderDescription instanceof HTMLElement) {
        return this.preorderDescription;
      }

      const host =
        this.buttonsBlock?.querySelector(".product__submit")
        || this.buttonsBlock;

      if (!(host instanceof HTMLElement)) {
        return null;
      }

      const description = document.createElement("p");
      description.className = "product__subs__description aw-main-product__preorder-description";
      description.dataset.awPreorderDescription = "";
      description.hidden = true;
      description.setAttribute("aria-hidden", "true");
      host.appendChild(description);
      this.preorderDescription = description;

      return description;
    }

    syncPreorderDescription() {
      const descriptionNode = this.ensurePreorderDescriptionNode();
      const variantId = this.getCurrentVariantId();
      const nextTextRaw = variantId ? this.preorderDescriptionMap?.[variantId] : "";
      const nextText = typeof nextTextRaw === "string" ? nextTextRaw.trim() : "";

      this.timesactController?.hideSellingPlanDescriptions();

      if (!(descriptionNode instanceof HTMLElement)) {
        return;
      }

      if (!nextText) {
        descriptionNode.textContent = "";
        descriptionNode.hidden = true;
        descriptionNode.setAttribute("aria-hidden", "true");
        return;
      }

      if (descriptionNode.textContent !== nextText) {
        descriptionNode.textContent = nextText;
      }

      descriptionNode.hidden = false;
      descriptionNode.setAttribute("aria-hidden", "false");
    }

    syncSellingPlanState() {
      this.preorderDescriptionMap = this.parsePreorderDescriptionMap();
      this.preorderPlanMap = this.parsePreorderPlanMap();
      this.variantAvailabilityMap = this.parseVariantAvailabilityMap();

      const variantState = this.getVariantState();
      const variantId = variantState.variantId;
      const planId = variantState.sellingPlanId;
      // Timesact creates a selling-plan allocation for one-time purchases too.
      // Its populated description is the reliable per-variant pre-order signal.
      const isPreorder = variantState.isPreorder;

      let sellingPlanInput = this.productForm?.querySelector("[data-aw-preorder-selling-plan]") || null;

      if (isPreorder && !(sellingPlanInput instanceof HTMLInputElement) && this.productForm instanceof HTMLFormElement) {
        sellingPlanInput = document.createElement("input");
        sellingPlanInput.type = "hidden";
        sellingPlanInput.name = "selling_plan";
        sellingPlanInput.dataset.awPreorderSellingPlan = "";
        this.productForm.appendChild(sellingPlanInput);
      }

      this.root.querySelectorAll('[name="selling_plan"]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) {
          return;
        }

        if (input === sellingPlanInput) {
          input.value = isPreorder ? planId : "";
          input.disabled = !isPreorder;
          return;
        }

        // The app can leave a previous variant's plan input in the form.
        // Keep it out of every request and submit the one AW-managed value above.
        input.value = "";
        input.disabled = true;
      });

      let preorderProperty = this.root.querySelector("[data-aw-preorder-property]");
      if (!(preorderProperty instanceof HTMLInputElement) && this.productForm instanceof HTMLFormElement) {
        preorderProperty = document.createElement("input");
        preorderProperty.type = "hidden";
        preorderProperty.dataset.awPreorderProperty = "";
        preorderProperty.name = `properties[${this.root.dataset.awPreorderPropertyName || "Sale type: Pre-order"}]`;
        preorderProperty.value = this.root.dataset.awPreorderPropertyValue || "Pre-order";
        this.productForm.appendChild(preorderProperty);
      }

      if (preorderProperty instanceof HTMLInputElement) {
        preorderProperty.disabled = !isPreorder;
      }

      const currentUrl = new URL(window.location.href);
      if (isPreorder) {
        currentUrl.searchParams.set("selling_plan", planId);
      } else {
        currentUrl.searchParams.delete("selling_plan");
      }

      if (currentUrl.href !== window.location.href) {
        window.history.replaceState({}, "", currentUrl.href);
      }

      const isSoldOut = variantState.available === false;
      const nativeAddButton = this.root.querySelector("[data-aw-add-button]");
      const soldOutLabel = nativeAddButton instanceof HTMLElement
        ? nativeAddButton.dataset.soldOutLabel || "Sold Out"
        : "Sold Out";
      const buttonLabel = isSoldOut ? soldOutLabel : isPreorder ? "予約する" : "カートに入れる";
      this.root.querySelectorAll("[data-add-to-cart-text]").forEach((node) => {
        if (node instanceof HTMLElement && node.textContent?.trim() !== buttonLabel) {
          node.textContent = buttonLabel;
        }
      });

      const primaryButton = this.getPrimaryActionButton();
      if (primaryButton instanceof HTMLElement && !this.timesactController?.isBisButton(primaryButton)) {
        primaryButton.setAttribute("aria-label", buttonLabel);
      }
    }

    shouldHandleChangeTarget(target) {
      if (!(target instanceof Element)) {
        return false;
      }

      if (target.closest(".gpo-container, .gpo-app, .gpo-element")) {
        return false;
      }

      if (
        target.matches('[name="quantity"], [name="id"], [name="selling_plan"], [name="selling-plan-group"], [data-popout-input]')
        || target.matches('input[name^="options["], select[name^="options["]')
      ) {
        return true;
      }

      return !!target.closest(".selector-wrapper, .product__block__subscription, .product__block__quantity");
    }

    shouldIgnoreMutations(mutations) {
      return mutations.length > 0 && mutations.every(({ target }) => {
        const element = target instanceof Element ? target : target?.parentElement;

        return element instanceof Element
          && !!element.closest(".gpo-container, .gpo-app, .gpo-element");
      });
    }

    openModal(modalId) {
      const modal = this.root.querySelector(`[data-aw-modal="${modalId}"]`);

      if (!(modal instanceof HTMLElement)) {
        return;
      }

      if (this.currentModal && this.currentModal !== modal) {
        this.closeModal(this.currentModal.id);
      }

      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");
      this.root.querySelectorAll(`[data-aw-modal-open="${modalId}"]`).forEach((button) => {
        button.setAttribute("aria-expanded", "true");
      });
      document.documentElement.classList.add("aw-main-product-modal-open");
      document.body.classList.add("aw-main-product-modal-open");
      this.currentModal = modal;
    }

    closeModal(modalId) {
      const modal = modalId
        ? this.root.querySelector(`[data-aw-modal="${modalId}"]`)
        : this.currentModal;

      if (!(modal instanceof HTMLElement)) {
        return;
      }

      modal.hidden = true;
      modal.setAttribute("aria-hidden", "true");
      this.root.querySelectorAll(`[data-aw-modal-open="${modal.id}"]`).forEach((button) => {
        button.setAttribute("aria-expanded", "false");
      });

      if (this.currentModal === modal) {
        this.currentModal = null;
      }

      if (!this.root.querySelector('.aw-main-product__modal[aria-hidden="false"]')) {
        document.documentElement.classList.remove("aw-main-product-modal-open");
        document.body.classList.remove("aw-main-product-modal-open");
      }
    }

    onCartChange() {
      if (!this.pendingCartRedirect) {
        return;
      }

      this.redirectToCart();
    }

    async getCartItemCount() {
      try {
        const response = await window.fetch(`${window.theme?.routes?.cart || "/cart"}.js`, {
          credentials: "same-origin",
          headers: {
            "X-Requested-With": "XMLHttpRequest"
          }
        });

        if (!response.ok) {
          return null;
        }

        const cart = await response.json();
        return Number.isFinite(cart?.item_count) ? cart.item_count : null;
      } catch (_error) {
        return null;
      }
    }

    prepareCartRedirect() {
      if (this.pendingCartRedirect) {
        return;
      }

      this.pendingCartRedirect = true;
      this.watchCartForRedirect();
    }

    async watchCartForRedirect() {
      const requestId = ++this.cartRedirectRequestId;
      const initialCount = await this.getCartItemCount();

      if (requestId !== this.cartRedirectRequestId || !this.pendingCartRedirect) {
        return;
      }

      let attempts = 0;

      const poll = async () => {
        if (requestId !== this.cartRedirectRequestId || !this.pendingCartRedirect) {
          return;
        }

        attempts += 1;
        const nextCount = await this.getCartItemCount();

        if (requestId !== this.cartRedirectRequestId || !this.pendingCartRedirect) {
          return;
        }

        if (
          initialCount !== null
          && nextCount !== null
          && nextCount > initialCount
        ) {
          this.redirectToCart();
          return;
        }

        if (attempts >= 10) {
          this.pendingCartRedirect = false;
          return;
        }

        this.cartRedirectTimeoutId = window.setTimeout(poll, 300);
      };

      this.cartRedirectTimeoutId = window.setTimeout(poll, 300);
    }

    redirectToCart() {
      this.pendingCartRedirect = false;

      if (this.cartRedirectTimeoutId) {
        window.clearTimeout(this.cartRedirectTimeoutId);
        this.cartRedirectTimeoutId = null;
      }

      window.location.href = window.theme?.routes?.cart || "/cart";
    }

    getPrimaryActionButton() {
      const timesactButton = this.timesactController?.getVisibleButton();

      if (timesactButton instanceof HTMLElement) {
        return timesactButton;
      }

      const candidates = Array.from(
        this.root.querySelectorAll(
          ".gpo-clone-atc-button, .gpo-original-atc-button, [data-add-to-cart], [name='add']"
        )
      );

      return candidates.find((button) => this.isElementVisible(button)) || null;
    }

    getButtonLabel(button) {
      if (!(button instanceof HTMLElement)) {
        return "";
      }

      const labelNode =
        button.querySelector("[data-add-to-cart-text]")
        || button.querySelector(".btn-state-ready")
        || button;
      const label = labelNode.textContent?.replace(/\s+/g, " ").trim() || "";

      return label;
    }

    syncStickyButtonLabel() {
      if (!(this.stickyAddButton instanceof HTMLButtonElement) || !(this.stickyAddButtonText instanceof HTMLElement)) {
        return;
      }

      const fallbackLabel = this.stickyAddButton.dataset.defaultLabel || this.stickyAddButtonText.textContent?.trim() || "";
      const currentLabel = this.getButtonLabel(this.getPrimaryActionButton()) || fallbackLabel;

      if (currentLabel) {
        this.stickyAddButtonText.textContent = currentLabel;
        this.stickyAddButton.setAttribute("aria-label", currentLabel);
        this.stickyAddButton.dataset.defaultLabel = currentLabel;
      }
    }

    isMobileViewport() {
      return window.matchMedia("(max-width: 767px)").matches;
    }

    isElementVisible(element) {
      if (!(element instanceof HTMLElement) || element.hidden) {
        return false;
      }

      const style = window.getComputedStyle(element);

      if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
        return false;
      }

      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }

    getScrollOffset() {
      const header = document.querySelector("header, .site-header, .header, .aw-header");

      if (!header) {
        return 96;
      }

      return header.getBoundingClientRect().height + 24;
    }

    scrollToVariantSelection() {
      if (!(this.variantTarget instanceof HTMLElement)) {
        return;
      }

      const top = this.variantTarget.getBoundingClientRect().top + window.pageYOffset - this.getScrollOffset();

      window.scrollTo({
        top,
        behavior: "smooth"
      });
    }

    updateStickyAddVisibility() {
      if (!(this.stickyAddWrap instanceof HTMLElement)) {
        return;
      }

      const primaryButton = this.getPrimaryActionButton();

      if (!this.isMobileViewport() || !this.isElementVisible(primaryButton)) {
        this.stickyAddWrap.classList.remove("is-visible");
        return;
      }

      const shouldShow = primaryButton.getBoundingClientRect().bottom < 0;
      this.stickyAddWrap.classList.toggle("is-visible", shouldShow);
    }

    observeImageResize() {
      if (this.imgResizeObserver) {
        this.imgResizeObserver.disconnect();
      }

      const activeImg = this.root.querySelector(".product__media.is-selected img") 
                      || this.root.querySelector(".product__media img");

      if (!activeImg || (!this.flickityViewport && !this.mediaStage)) return;

      this.imgResizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const currentImgHeight = entry.contentRect.height;

          if (currentImgHeight > 0) {
            let targetHeight = currentImgHeight;

            if (window.innerWidth > 1400) {
              targetHeight = Math.min(currentImgHeight, 800);
            }

            if (this.flickityViewport) {
              this.flickityViewport.style.height = `${targetHeight}px`;
            }
            if (this.mediaStage) {
              this.mediaStage.style.height = `${targetHeight}px`;
            }
          }
        }
      });

      this.imgResizeObserver.observe(activeImg);
    }

    sync() {
      this.refreshRefs();
      this.syncLegendValues();
      this.syncPreorderDescription();
      this.syncSellingPlanState();
      this.timesactController?.refresh();
      this.unisizeController?.refresh();
      this.syncMobileThumbFirstState();
      this.wishlistController?.refresh();
      this.syncStickyButtonLabel();
      this.updateStickyAddVisibility();
      this.observeImageResize();
    }
  }

  function initRoot(root) {
    if (!(root instanceof HTMLElement)) {
      return;
    }

    const sectionId = root.dataset.sectionId || `aw-main-product-${instances.size + 1}`;
    const existing = instances.get(sectionId);

    if (existing) {
      existing.destroy();
    }

    instances.set(sectionId, new AWMainProduct(root));
  }

  function destroyRoot(root) {
    if (!(root instanceof HTMLElement)) {
      return;
    }

    const sectionId = root.dataset.sectionId;
    const instance = sectionId ? instances.get(sectionId) : null;

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(sectionId);
  }

  function initAll(container = document) {
    container.querySelectorAll("[data-aw-main-product]").forEach(initRoot);
  }

  document.addEventListener("DOMContentLoaded", () => {
    initAll(document);
  });

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target.querySelector("[data-aw-main-product]");

    if (root) {
      destroyRoot(root);
    }
  });
})();
